// Success toast shown in the top-right corner once an item is really in the cart.
//
// The storefront adds to the cart from several places (the Horizon product form,
// the product page, collection grids, related products), and they do not share a
// single event. What they do share is the POST to /cart/add.js, so the toast
// watches that request instead of hooking every section separately.
//
// Flows that add an item and then send the customer straight to checkout call
// `window.cartToast.suppressNext()` first, so no toast flashes before the redirect.

const AUTO_DISMISS_DELAY = 6000;
const CART_ADD_PATH = /\/cart\/add(\.js)?(\?|$)/;
// Long enough to cover a slow add, short enough that an unused flag can't swallow a later one.
const SUPPRESS_TIMEOUT = 10000;

const toast = document.getElementById('cart-toast');

if (toast) {
  const productTarget = toast.querySelector('[data-cart-toast-product]');

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let dismissTimeout;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let suppressTimeout;
  let suppressCount = 0;
  // Guards the delayed "now really hide it" step against a toast that reopened meanwhile.
  let showToken = 0;

  const clearDismissTimer = () => {
    clearTimeout(dismissTimeout);
    dismissTimeout = undefined;
  };

  const hide = () => {
    clearDismissTimer();
    if (toast.hidden) return;

    toast.classList.remove('cart-toast-popup--visible');

    const token = ++showToken;
    const onHidden = () => {
      if (token !== showToken) return;
      toast.hidden = true;
    };

    toast.addEventListener('transitionend', onHidden, { once: true });
    // Fallback for when no transition runs, e.g. reduced motion or a background tab.
    setTimeout(onHidden, 400);
  };

  const startDismissTimer = () => {
    clearDismissTimer();
    dismissTimeout = setTimeout(hide, AUTO_DISMISS_DELAY);
  };

  /**
   * Shows the toast, naming the product that was added.
   * @param {string} [productName] - Name to display. Empty leaves the line out.
   */
  const show = (productName = '') => {
    showToken++;

    if (productTarget) productTarget.textContent = productName;

    toast.hidden = false;
    // Force a reflow so the browser animates in from the hidden state.
    void toast.offsetWidth;
    toast.classList.add('cart-toast-popup--visible');

    startDismissTimer();
  };

  /**
   * Skips the toast for the next add, for flows that redirect to checkout instead.
   */
  const suppressNext = () => {
    suppressCount++;
    clearTimeout(suppressTimeout);
    suppressTimeout = setTimeout(() => {
      suppressCount = 0;
    }, SUPPRESS_TIMEOUT);
  };

  /**
   * Reads the product name out of a /cart/add.js response body.
   * @param {any} payload - The parsed response.
   * @returns {string} The name, or an empty string when the shape is unexpected.
   */
  const productNameFrom = (payload) => {
    if (!payload || payload.status) return '';

    const item = Array.isArray(payload.items) ? payload.items[0] : payload;
    if (!item) return '';

    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

    return [item.product_title || item.title, variantTitle].filter(Boolean).join(' · ');
  };

  /**
   * Resolves the request URL regardless of how fetch was called.
   * @param {RequestInfo | URL} input - The first fetch argument.
   * @returns {string} The URL, or an empty string.
   */
  const urlOf = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input instanceof Request) return input.url;
    return '';
  };

  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const response = originalFetch.apply(this, args);

    if (!CART_ADD_PATH.test(urlOf(args[0]))) return response;

    if (suppressCount > 0) {
      suppressCount--;
      return response;
    }

    response
      .then((result) => {
        if (!result.ok) return;

        return result
          .clone()
          .json()
          .then((payload) => show(productNameFrom(payload)));
      })
      .catch(() => {
        // A failed add already surfaces its own error, so stay quiet here.
      });

    return response;
  };

  toast.addEventListener('click', (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest('[data-cart-toast-close]')) hide();
  });

  // Hold the toast open while the customer is reading it or tabbing through it.
  toast.addEventListener('pointerenter', clearDismissTimer);
  toast.addEventListener('focusin', clearDismissTimer);
  toast.addEventListener('pointerleave', () => {
    if (!toast.hidden) startDismissTimer();
  });
  toast.addEventListener('focusout', (event) => {
    if (toast.hidden) return;
    if (toast.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    startDismissTimer();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });

  window.cartToast = { show, hide, suppressNext };
}
